import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import nfceApi from '../../services/nfceApi.js';
import { colors } from '../../utils/colors.js';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
`;

const List = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid ${colors.gray[100]};

  &:last-child {
    border-bottom: none;
  }
`;

const RowLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Nome = styled.span`
  font-weight: 600;
  color: ${colors.gray[900]};
`;

const Meta = styled.span`
  font-size: 13px;
  color: ${colors.gray[500]};
`;

const Badge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${colors.gray[100]};
  color: ${colors.gray[600]};
`;

const StatusBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(props) => (props.$status === 'habilitado' ? colors.success.light : colors.warning.light)};
  color: ${(props) => (props.$status === 'habilitado' ? colors.success.dark : colors.warning.dark)};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.gray[500]};
  font-size: 14px;
  padding: 6px;

  &:hover {
    color: ${colors.primary[600]};
  }
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: ${colors.gray[500]};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.form`
  background: ${colors.white};
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  max-width: 420px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 20px;
  color: ${colors.gray[900]};
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
`;

const CancelButton = styled(Button)`
  background: ${colors.gray[100]};
  color: ${colors.gray[700]};
`;

const SaveButton = styled(Button)`
  background: ${colors.gradients.primary};
  color: ${colors.white};
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const TIPO_INTEGRACAO_LABEL = {
  integrado: 'Integrado (webhook)',
  automatico: 'Automático (scraping)',
  manual: 'Manual',
};

const emptyForm = { uf: '', modelo_nf: '65', tipo_integracao: 'manual', status: 'desabilitado', status_integracao: 'inativo' };

export default function NfceHomologacaoManager() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await nfceApi.listHomologacaoConfig();
      setConfigs(data || []);
    } catch (err) {
      // silencioso: a lista simplesmente fica vazia
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (config) => {
    setEditing(config);
    setForm({
      uf: config.uf,
      modelo_nf: config.modelo_nf,
      tipo_integracao: config.tipo_integracao,
      status: config.status,
      status_integracao: config.status_integracao,
    });
    setFormError('');
    setShowModal(true);
  };

  const handleToggleStatus = async (config) => {
    const novoStatus = config.status === 'habilitado' ? 'desabilitado' : 'habilitado';
    try {
      await nfceApi.updateHomologacaoConfig(config.id, { status: novoStatus });
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível alterar o status.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      if (editing) {
        await nfceApi.updateHomologacaoConfig(editing.id, {
          tipo_integracao: form.tipo_integracao,
          status: form.status,
          status_integracao: form.status_integracao,
        });
      } else {
        await nfceApi.createHomologacaoConfig({
          uf: form.uf.toUpperCase(),
          modelo_nf: form.modelo_nf,
          tipo_integracao: form.tipo_integracao,
          status: form.status,
          status_integracao: form.status_integracao,
        });
      }
      setShowModal(false);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Não foi possível salvar a configuração.');
    }
  };

  return (
    <div>
      <Header>
        <Title>Homologação NFCe por UF</Title>
        <AddButton type="button" onClick={openCreate}>
          <FaPlus /> Nova UF
        </AddButton>
      </Header>

      <List>
        {loading && <Empty>Carregando...</Empty>}
        {!loading && configs.length === 0 && <Empty>Nenhuma UF homologada ainda.</Empty>}
        {!loading &&
          configs.map((config) => (
            <Row key={config.id}>
              <RowLeft>
                <RowTop>
                  <Nome>{config.uf}</Nome>
                  <Badge>Modelo {config.modelo_nf}</Badge>
                  <Badge>{TIPO_INTEGRACAO_LABEL[config.tipo_integracao] || config.tipo_integracao}</Badge>
                  <StatusBadge $status={config.status}>
                    {config.status === 'habilitado' ? 'habilitado' : config.status}
                  </StatusBadge>
                </RowTop>
                <Meta>Integração: {config.status_integracao}</Meta>
              </RowLeft>
              <Actions>
                <IconButton
                  type="button"
                  onClick={() => handleToggleStatus(config)}
                  title={config.status === 'habilitado' ? 'Desabilitar' : 'Habilitar'}
                >
                  {config.status === 'habilitado' ? <FaToggleOn /> : <FaToggleOff />}
                </IconButton>
                <IconButton type="button" onClick={() => openEdit(config)} title="Editar">
                  <FaEdit />
                </IconButton>
              </Actions>
            </Row>
          ))}
      </List>

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <ModalTitle>{editing ? `Editar ${editing.uf}` : 'Nova UF'}</ModalTitle>

            {formError && <ErrorBox>{formError}</ErrorBox>}

            {!editing && (
              <>
                <Label htmlFor="uf">UF (sigla)</Label>
                <Input
                  id="uf"
                  value={form.uf}
                  onChange={(e) => setForm((f) => ({ ...f, uf: e.target.value.slice(0, 2) }))}
                  maxLength={2}
                  required
                  autoFocus
                />

                <Label htmlFor="modelo_nf">Modelo do documento</Label>
                <Input
                  id="modelo_nf"
                  value={form.modelo_nf}
                  onChange={(e) => setForm((f) => ({ ...f, modelo_nf: e.target.value.slice(0, 2) }))}
                  maxLength={2}
                  required
                />
              </>
            )}

            <Label htmlFor="tipo_integracao">Tipo de integração</Label>
            <Select
              id="tipo_integracao"
              value={form.tipo_integracao}
              onChange={(e) => setForm((f) => ({ ...f, tipo_integracao: e.target.value }))}
            >
              <option value="manual">Manual</option>
              <option value="automatico">Automático (scraping)</option>
              <option value="integrado">Integrado (webhook)</option>
            </Select>

            <Label htmlFor="status">Status</Label>
            <Select
              id="status"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="desabilitado">Desabilitado</option>
              <option value="habilitado">Habilitado</option>
              <option value="em_teste">Em teste</option>
            </Select>

            <Label htmlFor="status_integracao">Status da integração</Label>
            <Select
              id="status_integracao"
              value={form.status_integracao}
              onChange={(e) => setForm((f) => ({ ...f, status_integracao: e.target.value }))}
            >
              <option value="inativo">Inativo</option>
              <option value="ativo">Ativo</option>
              <option value="manutencao">Manutenção</option>
            </Select>

            <ModalActions>
              <CancelButton type="button" onClick={() => setShowModal(false)}>
                Cancelar
              </CancelButton>
              <SaveButton type="submit">Salvar</SaveButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </div>
  );
}
